import React, { createContext, useState, useCallback, useEffect, useRef } from "react";

const OrdersContext = createContext({
  ordersCount: 0,
  activeOrdersCount: 0,
  fetchOrdersCount: () => {},
  setOrdersCount: () => {},
  orders: null,
  customerOrders: null,
  ordersLoading: false,
  fetchOrders: () => {},
  setOrders: () => {},
  setCustomerOrders: () => {},
});

export const OrdersProvider = ({ children }) => {
  const [ordersCount,       setOrdersCount]       = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);

  // null = not fetched yet, [] = fetched but empty
  // Orders.js uses null to distinguish "still loading" from "genuinely empty"
  const [orders,         setOrders]         = useState(null);
  const [customerOrders, setCustomerOrders] = useState(null);
  const [ordersLoading,  setOrdersLoading]  = useState(false);
  const isFetchingRef = useRef(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  const calculateActiveOrders = (data) =>
    data.filter(o => {
      const s = (o.status || '').toLowerCase();
      return s === 'processing' || s === 'out for delivery';
    }).length;

  // ── Single fetch: get_orders + customer_orders in parallel ───────────────
  // Called on app load so data is warm before user navigates to Orders page.
  // Cache-first: subsequent calls are no-ops unless force=true.
  const fetchOrders = useCallback(async ({ force = false } = {}) => {
    const token  = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    if (!token || !userId || userId === 'undefined' || userId === 'null') return;

    // Cache hit — skip unless forced (e.g. after place_order / cancel)
    if (!force && orders !== null && customerOrders !== null) return;

    // Deduplicate concurrent calls (StrictMode double-invoke, interval + mount race)
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setOrdersLoading(true);

    const start = performance.now();
    try {
      const [userRes, customerRes] = await Promise.all([
        fetch(`${API_URL}/api/get_orders?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_URL}/api/customer_orders?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      const elapsed = performance.now() - start;
      if (elapsed > 2000) {
        console.error(
          `[SLOW][OrdersContext] fetchOrders took ${(elapsed / 1000).toFixed(2)}s — ` +
          `get_orders=${userRes.status} customer_orders=${customerRes.status}`
        );
      }

      const [userData, customerData] = await Promise.all([
        userRes.ok     ? userRes.json()     : [],
        customerRes.ok ? customerRes.json() : []
      ]);

      setOrders(userData);
      setCustomerOrders(customerData);
      setOrdersCount(userData.length);
      setActiveOrdersCount(calculateActiveOrders(userData));
    } catch (e) {
      console.error('[OrdersContext] fetchOrders failed:', e);
    } finally {
      setOrdersLoading(false);
      isFetchingRef.current = false;
    }
  }, [API_URL, orders, customerOrders]);

  // fetchOrdersCount kept for backward compat — delegates to fetchOrders
  const fetchOrdersCount = useCallback(
    ({ force = false } = {}) => fetchOrders({ force }),
    [fetchOrders]
  );

  // Ref so interval always calls the latest fetchOrders closure
  const fetchRef = useRef(fetchOrders);
  fetchRef.current = fetchOrders;

  useEffect(() => {
    // Eagerly pre-fetch on app load — Orders page renders instantly on first visit
    fetchRef.current();
    // Refresh every 60s so status stays current without user having to reload
    const intervalId = setInterval(() => fetchRef.current({ force: true }), 60000);
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user_id') fetchRef.current({ force: true });
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <OrdersContext.Provider value={{
      ordersCount,       setOrdersCount,
      activeOrdersCount,
      fetchOrdersCount,
      orders,            setOrders,
      customerOrders,    setCustomerOrders,
      ordersLoading,
      fetchOrders,
    }}>
      {children}
    </OrdersContext.Provider>
  );
};

export default OrdersContext;